import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Title, useGetList, useNotify, usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage, todayLocalYmd } from "../shared/api-error";
import { downloadTextFile } from "../shared/csv";
import { formatRupees } from "../shared/fee-format";
import { MADRASA_FEES_VIEW } from "../shared/madrasa-permissions";
import {
  collectionToCsv,
  defaultSummaryRange,
  MODE_LABEL,
  MODE_ORDER,
  summaryIsInconsistent,
  type CollectionSummary,
} from "./report-utils";

type YearRow = { id: string; name: string; isCurrent: boolean; startDate: string; endDate: string };
type ClassRow = { id: string; name: string };
const ALL = "ALL";

/** Fees collected in a date range, by class and payment mode. Voided receipts are not counted. */
export default function CollectionSummaryPage() {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const years = useGetList<YearRow>("academicYears", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "startDate", order: "DESC" },
  });
  const classes = useGetList<ClassRow>("madrasaClasses", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sortOrder", order: "ASC" },
  });
  const [range, setRange] = useState<{ dateFrom: string; dateTo: string } | null>(null);
  const [classId, setClassId] = useState(ALL);
  const [summary, setSummary] = useState<CollectionSummary | null>(null);
  const [loading, setLoading] = useState(false);

  // Start on the current academic year's start, up to today.
  useEffect(() => {
    if (range || years.isPending) return;
    const current = years.data?.find((y) => y.isCurrent) ?? years.data?.[0];
    setRange(defaultSummaryRange(current, todayLocalYmd()));
  }, [years.data, years.isPending, range]);

  useEffect(() => {
    if (!range || !range.dateFrom || !range.dateTo) return;
    setLoading(true);
    callApi({
      location: "feeReports/collection-summary",
      method: "GET",
      data: { ...range, ...(classId !== ALL ? { classId } : {}) },
    })
      .then((r) => setSummary(r.data as CollectionSummary))
      .catch((error: unknown) => {
        setSummary(null);
        notify(apiErrorMessage(error, "Could not load the summary"), { type: "error" });
      })
      .finally(() => setLoading(false));
  }, [range, classId, notify]);

  if (!hasPermission(permissions, MADRASA_FEES_VIEW)) return null;

  return (
    <>
      <Title title="Fee collection summary" />
      <Box sx={{ p: 2, maxWidth: 1000 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Fee collection summary
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Money collected between two dates, by class and payment mode.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, alignItems: { sm: "center" } }}
        >
          <TextField
            label="From"
            type="date"
            value={range?.dateFrom ?? ""}
            onChange={(e) =>
              setRange((r) => ({ dateFrom: e.target.value, dateTo: r?.dateTo ?? e.target.value }))
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="To"
            type="date"
            value={range?.dateTo ?? ""}
            onChange={(e) =>
              setRange((r) => ({ dateFrom: r?.dateFrom ?? e.target.value, dateTo: e.target.value }))
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            select
            label="Class"
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value={ALL}>All classes</MenuItem>
            {(classes.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            disabled={!summary}
            onClick={() =>
              summary &&
              downloadTextFile(
                `fee-collection-${summary.dateFrom}-to-${summary.dateTo}.csv`,
                collectionToCsv(summary)
              )
            }
          >
            Export CSV
          </Button>
        </Stack>

        {loading && <CircularProgress size={28} />}
        {summary && !loading && (
          <>
            {summaryIsInconsistent(summary) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                The class breakdown ({formatRupees(summary.gridTotal)}) does not match the total (
                {formatRupees(summary.totalCollected)}). Please report this.
              </Alert>
            )}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h5">{formatRupees(summary.totalCollected)}</Typography>
              <Typography variant="body2" color="text.secondary">
                collected in {summary.receiptCount}{" "}
                {summary.receiptCount === 1 ? "receipt" : "receipts"}
                {summary.voidedCount > 0 &&
                  ` · ${summary.voidedCount} voided ${summary.voidedCount === 1 ? "receipt" : "receipts"} (${formatRupees(summary.voidedAmount)}) not counted`}
              </Typography>
            </Paper>

            {summary.byClass.length === 0 ? (
              <Alert severity="info">No fees were collected in this period.</Alert>
            ) : (
              <>
                <Table size="small" component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Class</TableCell>
                      {MODE_ORDER.map((m) => (
                        <TableCell key={m} align="right">
                          {MODE_LABEL[m]}
                        </TableCell>
                      ))}
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.byClass.map((c) => (
                      <TableRow key={c.classId}>
                        <TableCell>{c.className}</TableCell>
                        {MODE_ORDER.map((m) => (
                          <TableCell key={m} align="right">
                            {c.byMode[m] ? formatRupees(c.byMode[m]) : "—"}
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          <strong>{formatRupees(c.total)}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>
                        <strong>All classes</strong>
                      </TableCell>
                      {MODE_ORDER.map((m) => (
                        <TableCell key={m} align="right">
                          <strong>
                            {summary.byMode[m] ? formatRupees(summary.byMode[m]) : "—"}
                          </strong>
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        <strong>{formatRupees(summary.totalCollected)}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  By fee
                </Typography>
                <Table size="small" component={Paper} variant="outlined" sx={{ maxWidth: 420 }}>
                  <TableBody>
                    {summary.byFeeHead.map((f) => (
                      <TableRow key={f.feeHeadName}>
                        <TableCell>{f.feeHeadName}</TableCell>
                        <TableCell align="right">{formatRupees(f.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
}
